/**
 * useCreativeLibrary - Fetches creative assets from Supabase
 *
 * Uses React Query with 5-minute staleTime to avoid refetching
 * the entire library on every cell click.
 * Generates batch signed URLs for thumbnail_path (falls back to thumbnail_url).
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSignedThumbnailUrls, resolveThumbnailUrl } from "./useSignedThumbnailUrls";
import { useMemo } from "react";

export interface Creative {
  id: string;
  filename: string;
  thumbnailUrl: string | null;
  thumbnailPath: string | null;
  type: "image" | "video";
  aspectRatioCategory: "feed" | "vertical" | "horizontal" | null;
  dimensions: string | null;
  metaCreativeId: string;
  metaAccountId: string;
  groupId: string | null;
  groupType: string | null;
  tags: Record<string, string> | null;
}

export interface CreativeLibraryResult {
  creatives: Creative[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

export function useCreativeLibrary(adAccountId?: string): CreativeLibraryResult {
  const { user } = useAuth();

  const {
    data: rawCreatives = [],
    isLoading: isLoadingCreatives,
    error,
    refetch,
  } = useQuery({
    queryKey: ["creative-library", user?.id, adAccountId],
    queryFn: async (): Promise<Creative[]> => {
      if (!user?.id) return [];

      let query = supabase
        .from("creative_metadata")
        .select("*, tags")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      // Filter by ad account if provided
      if (adAccountId) {
        query = query.eq("meta_account_id", adAccountId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching creatives:", error);
        throw error;
      }

      // Map database rows to Creative interface
      return (data || []).map((row) => ({
        id: row.id,
        filename: row.filename,
        thumbnailUrl: row.thumbnail_url,
        thumbnailPath: row.thumbnail_path || null,
        type: (row.creative_type === "video" ? "video" : "image") as "image" | "video",
        aspectRatioCategory: row.aspect_ratio_category as Creative["aspectRatioCategory"],
        dimensions: row.dimensions,
        metaCreativeId: row.meta_creative_id,
        metaAccountId: row.meta_account_id,
        groupId: row.group_id,
        groupType: row.group_type || null,
        tags: (row.tags as Record<string, string>) || null,
      }));
    },
    enabled: !!user?.id,
    staleTime: STALE_TIME,
    gcTime: STALE_TIME * 2, // Keep in cache for 10 minutes
  });

  // Generate signed URLs for all thumbnail paths
  const paths = useMemo(() => rawCreatives.map((c) => c.thumbnailPath), [rawCreatives]);
  const { urlMap, isLoading: isLoadingUrls } = useSignedThumbnailUrls(paths);

  // Resolve signed URLs into creatives
  const creatives = useMemo(() => {
    return rawCreatives.map((c) => ({
      ...c,
      thumbnailUrl: resolveThumbnailUrl(urlMap, c.thumbnailPath, c.thumbnailUrl),
    }));
  }, [rawCreatives, urlMap]);

  return {
    creatives,
    isLoading: isLoadingCreatives || isLoadingUrls,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Helper to filter creatives by aspect ratio category
 */
export function filterCreativesByAspect(
  creatives: Creative[],
  category: "feed" | "vertical" | "horizontal" | "all"
): Creative[] {
  if (category === "all") return creatives;
  return creatives.filter((c) => c.aspectRatioCategory === category);
}

/**
 * Helper to filter creatives by type
 */
export function filterCreativesByType(
  creatives: Creative[],
  type: "image" | "video" | "all"
): Creative[] {
  if (type === "all") return creatives;
  return creatives.filter((c) => c.type === type);
}
